import { ProductImageUploadView } from './product_image_upload_view';
import { ProductImages } from './product_images_model';
import {
  allowedImageUploads,
  getExtensionFromFilename,
  IMG_FALLBACK_WIDTH,
  isJPEG2000File,
  parseImageUsage,
} from './utils';

/** ProductImageGridView - provides the look-and-feel for managing images in a product image, lightbox grid view */
export class ProductImageGridView implements MerchantosProductImageGridView {
  private tiledViewTemplate: string;

  private imageTemplate: string;

  private imageContainerTemplate: string;

  private featuredTemplate: string;

  private uploadingTemplate: string;

  // @ts-expect-error ts-migrate(6133) FIXME: 'id' is declared but its value is never read.
  private id: string;

  private productImages: any;

  public $gridView: JQuery<HTMLElement>;

  private uploadView?: ProductImageUploadView;

  private currentUploads: { [uploadId: string]: JQuery } = {};

  private isFirefox: boolean;

  private maxImages: number;

  private uploadsInProgress: number;

  private options: {
    type: string;
    imageType: ImageType;
    productMatrix: boolean;
    readonly: boolean;
  };

  private uploadTemplate = `<div class="image-upload-container-outer"><div class="image-upload-container"><div class="upload upload-modal" id="product-image-grid-upload"><div class="uploads" style="display:none;"><div class="uploads-progress"></div><div class="uploads-complete"><span class="dz-uploads-complete" notranslate></span><span class="dz-uploads-complete-text"> of </span><span class="dz-uploads-total" notranslate></span><span class="dz-uploads-complete-text"> images completed</span></div></div><div class="drop-area-modal"><div class="dz-message-modal">`;

  constructor(
    productId: string,
    productImages: ProductImages,
    options: Partial<ProductImageGridView['options']>
  ) {
    this.tiledViewTemplate =
      '<div class="product-image-lightbox"><div class="product-grid-menu"><a class="close" href="#"><i class="icon-remove"></i> Close</a><a class="slideshow-view" href="#"><i class="icon-picture"></i> Slideshow</a></div><div class="product-image-grid"><ol class="product-image-list"></ol></div></div>';
    this.imageTemplate = '<li class="image"></li>';
    this.imageContainerTemplate =
      '<div class="image-container"><div class="image-wrapper"><img class="image-reference"></div></div>';
    this.featuredTemplate =
      '<div class="featured-outer"><div class="featured-inner">Featured</div></div>';
    this.uploadingTemplate =
      '<div class="product-image-upload"><span class="product-image-upload-progress"></span><div class="product-image-filename"></div></div>';

    this.id = productId;
    this.productImages = productImages;
    this.$gridView = $();
    this.isFirefox = typeof window.InstallTrigger !== 'undefined';
    this.maxImages = 12;
    this.uploadsInProgress = 0;

    this.options = {
      type: 'Item',
      imageType: 'Image',
      productMatrix: false,
      readonly: false,
      ...options,
    };

    const imageUsage = parseImageUsage(window.merchantos.session.imageUsage?.Usage!);

    let countLimitRatio;
    let sizeLimitRatio;
    if (
      imageUsage.totalImageSizeHardLimitReached === 'true' ||
      imageUsage.imageCountHardLimitReached === 'true'
    ) {
      this.uploadTemplate = this.uploadTemplate.replace(
        '<div class="upload upload-modal"',
        '<div class="upload upload-modal disabled"'
      );

      countLimitRatio = imageUsage.imageCount / imageUsage.defaultMaxImageCount;
      sizeLimitRatio = imageUsage.totalImageSize / imageUsage.defaultMaxTotalImageSize;

      if (countLimitRatio > sizeLimitRatio) {
        this.uploadTemplate +=
          `<var>${imageUsage.imageCount}</var> of ` +
          `<var>${imageUsage.defaultMaxImageCount}</var> images used.`;
      } else {
        this.uploadTemplate +=
          `<var>${Math.floor(imageUsage.totalImageSize / 1024 ** 3)}</var> GB of ` +
          `<var>${Math.floor(imageUsage.defaultMaxTotalImageSize / 1024 ** 3)}</var> GB used.`;
      }

      this.uploadTemplate +=
        '<br />Image upload disabled.<br /><a href="https://retail-support.lightspeedhq.com/hc/articles/229130008-Adding-images-to-items">Learn more</a>.' +
        '</div>';
    } else {
      this.uploadTemplate +=
        'Drag and drop files<br>here to upload</div><button>Browse Files</button>';

      if (
        imageUsage.totalImageSizeSoftLimitReached === 'true' ||
        imageUsage.imageCountSoftLimitReached === 'true'
      ) {
        this.uploadTemplate += '<div class="image-limit-modal">';

        countLimitRatio = imageUsage.imageCount / imageUsage.defaultMaxImageCount;
        sizeLimitRatio = imageUsage.totalImageSize / imageUsage.defaultMaxTotalImageSize;

        if (countLimitRatio > sizeLimitRatio) {
          this.uploadTemplate +=
            `<var>${imageUsage.imageCount}</var> of ` +
            `<var>${imageUsage.defaultMaxImageCount}</var> images used.`;
        } else {
          this.uploadTemplate +=
            `<var>${Math.floor(imageUsage.totalImageSize / 1024 ** 3)}</var>GB of ` +
            `<var>${Math.floor(imageUsage.defaultMaxTotalImageSize / 1024 ** 3)}</var> GB used.`;
        }

        this.uploadTemplate +=
          '<br /><a href="https://retail-support.lightspeedhq.com/hc/articles/229130008-Adding-images-to-items">Learn More</a>' +
          '</div>';
      }
    }

    this.uploadTemplate += '</div></div></div></div>';

    this.render();
  }

  initEventHandlers: MerchantosProductImageGridView['initEventHandlers'] = () => {
    const $closeButton = this.$gridView.find('a.close');
    $closeButton.on('click', () => {
      $('body').removeClass('lockscroll');
      this.$gridView.trigger('closed');
      this.$gridView.remove();
      if (!this.options.readonly) {
        this.uploadView!.disable();
      }
      $(document).off('keyup.gridview');
      $(document).off('touchmove');
    });

    $('div.product-image-lightbox').on('click', (event: JQuery.ClickEvent) => {
      const $target = $(event.target);
      // prevent child objects from triggering the event through propagation
      if ($target.hasClass('product-image-lightbox')) {
        $closeButton.trigger('click');
      }
    });
    $(document).one('keyup.gridview', (event: JQuery.TriggeredEvent) => {
      if (event.keyCode === 27) {
        $closeButton.trigger('click');
      }
    });

    const $imageTiles = this.$gridView
      .find('li.image div.image-container')
      .not('.product-image-blank');
    $imageTiles.off('click').on('click', (event) => {
      $('body').removeClass('lockscroll');

      const imageId = parseInt(
        $(event!.currentTarget!).parent().attr('imageid') ||
          this.$gridView.find('[imageid]:first').attr('imageid')!,
        10
      );

      this.$gridView.trigger('imageselected', imageId);
      if (!this.options.readonly) {
        this.uploadView!.disable();
      }
      $(document).off('keyup.gridview');
      $(document).off('touchmove');
    });

    const $slideshowButton = this.$gridView.find('a.slideshow-view');
    $slideshowButton.on('click', () => $imageTiles.first().trigger('click'));

    if (!this.options.readonly) {
      const $uploadTile = this.$gridView.find('div.upload').parent();
      $uploadTile.off('fileadded percentcomplete uploadcomplete errorOccurred');
      $uploadTile.on('fileadded', (_event, data) =>
        this.startUpload(data.uploadId, data.filename, data.file)
      );
      $uploadTile.on('percentcomplete', (_event, data) =>
        this.updateUploadProgress(data.uploadId, data.progress)
      );
      $uploadTile.on('uploadcomplete', (_event, data) =>
        this.finishUpload(data.uploadId, data.response)
      );
      $uploadTile.on('errorOccurred', (event, data) => {
        event.stopPropagation();
        const fileName = data.file?.name?.split('.')?.[0];
        $(`var:contains(${fileName})`).closest('li').remove();
      });
    }
  };

  startUpload: MerchantosProductImageGridView['startUpload'] = (uploadId, filename, _file) => {
    this.uploadsInProgress += 1;

    let $firstFreeTile = this.$gridView
      .find('div.product-image-blank, div.image-upload-container-outer')
      .not('.product-image-uploading')
      .first();
    if ($firstFreeTile.hasClass('image-upload-container-outer')) {
      $firstFreeTile.hide();

      const $imgContainer = $(this.imageContainerTemplate)
        .addClass('product-image-blank')
        .appendTo($firstFreeTile.parent());

      $firstFreeTile = $imgContainer;
    }
    $firstFreeTile.addClass('product-image-uploading');

    const $uploadTemplate = $(this.uploadingTemplate);
    $uploadTemplate.find('.product-image-filename').html(`Uploading<br><var>${filename}</var>`);
    $firstFreeTile.append($uploadTemplate);

    this.currentUploads[uploadId] = $firstFreeTile;

    this.handleGridResize($firstFreeTile);
  };

  updateUploadProgress: MerchantosProductImageGridView['updateUploadProgress'] = (
    uploadId,
    progress
  ) => {
    const $uploadTile = this.currentUploads[uploadId];
    if ($uploadTile && $uploadTile.length) {
      $uploadTile.find('.product-image-upload-progress').css('width', `${progress}%`);
    }
  };

  finishUpload: MerchantosProductImageGridView['finishUpload'] = (uploadId, response) => {
    this.uploadsInProgress -= 1;

    const $uploadTile = this.currentUploads[uploadId].removeClass(
      'product-image-blank product-image-uploading'
    );
    if ($uploadTile.length) {
      $uploadTile.find('.product-image-upload').remove();
    }

    const $imageData = $($.parseXML(response)).find(this.options.imageType);
    const image: ImageInfo = {
      imageID: Number($imageData.find('imageID, workorderImageID').text()),
      description: $imageData.find('description').text(),
      filename: $imageData.find('filename').text(),
      publicID: $imageData.find('publicID').text(),
      itemId: $imageData.find('itemID').text(),
      workorderID: $imageData.find('workorderID').text(),
      itemMatrixID: $imageData.find('itemMatrixID').text(),
      ordering: Number($imageData.find('ordering').text()),
    };

    this.productImages.addProductImage(image);
    this.productImages.cacheProductImage(image);

    // As browsers don't support JPEG2000, when JPEG2000 file is uploaded we request .webp file instead from the server to display
    const originalExtension = getExtensionFromFilename(image.filename);
    const fallbackExtension = allowedImageUploads.WEBP.extension[0].replace('.', '');

    const imageId = Number($imageData.find('imageID, workorderImageID').text());
    const productImage = this.productImages.imageDict[imageId];
    const $image = $uploadTile.parent().attr('imageid', imageId).find('.image-container');
    const imgHeight = $image.height() ?? IMG_FALLBACK_WIDTH;
    const imgWidth = $image.width() ?? IMG_FALLBACK_WIDTH;

    $uploadTile
      .parent()
      .attr('imageid', imageId)
      .find('img')
      .attr(
        'src',
        productImage.getResizedUrl({
          format: isJPEG2000File({ filename: image.filename })
            ? fallbackExtension
            : originalExtension ?? fallbackExtension,
          crop: 'fill',
          width: Math.round(imgWidth),
          height: Math.round(imgHeight),
        })
      );

    this.initEventHandlers(); // re-attach event handlers for the new image

    this.enableDragDrop($uploadTile.parent());

    delete this.currentUploads[uploadId];
  };

  drop: MerchantosProductImageGridView['drop'] = (event) => {
    event.preventDefault();
    const data = event.originalEvent?.dataTransfer?.getData('itemID');
    const $source = $(`li.image[imageid=${data}]`);
    const $destination = $(event.currentTarget);
    const sourceImage = this.productImages.imageDict[$source.attr('imageid')!];
    const destinationImage = this.productImages.imageDict[$destination.attr('imageid')!];

    if (sourceImage !== destinationImage) {
      if ($source.nextAll().filter($destination).length) {
        $source.insertAfter($destination);
        this.productImages.images.insertAfter(sourceImage, destinationImage);
      } else {
        $source.insertBefore($destination);
        this.productImages.images.insertBefore(sourceImage, destinationImage);
      }

      let $featuredElement = $source.find('div.featured-outer');
      if ($featuredElement.length) {
        $featuredElement.prependTo($source.siblings().first());
      } else {
        $featuredElement = $destination.find('div.featured-outer');
        if ($featuredElement.length) {
          $featuredElement.prependTo($source);
        }
      }
    }
  };

  allowDrop: MerchantosProductImageGridView['allowDrop'] = (event) => event.preventDefault();

  dragstart: MerchantosProductImageGridView['dragstart'] = (event) => {
    if (this.isFirefox) {
      // we use a wrapper of the same size as the image otherwise the padding from the image-container
      // div will be shown as well
      const $dragFeedback = $(event.currentTarget).find('.image-wrapper');
      const dragFeedbackHeight = $dragFeedback.height()!;
      const dragFeedbackWidth = $dragFeedback.width()!;
      event.originalEvent!.dataTransfer!.setDragImage(
        $dragFeedback[0],
        dragFeedbackWidth,
        dragFeedbackHeight
      );
    }
    event.originalEvent!.dataTransfer!.setData('itemID', $(event.currentTarget).attr('imageid')!);
  };

  enableDragDrop: MerchantosProductImageGridView['enableDragDrop'] = ($image) => {
    if (!this.options.readonly) {
      $image.on('drop', this.drop.bind(this));
      $image.on('dragstart', this.dragstart);
      $image.on('dragover', this.allowDrop);
    }
  };

  render: MerchantosProductImageGridView['render'] = () => {
    const $body = $('body').addClass('lockscroll');

    // add the grid view overlay
    const $lightboxView = $('div.product-image-lightbox');
    if ($lightboxView.length) {
      $lightboxView.remove();
    }
    this.$gridView = $(this.tiledViewTemplate).prependTo($body);
    const $tiledList = this.$gridView.find('.product-image-list');

    const numTiles = this.numberOfTilesToDisplay();

    let currentImage = this.productImages.images.head;
    let i = 0;
    while (currentImage !== null) {
      const $image = $(this.imageTemplate).attr('imageid', currentImage.id).appendTo($tiledList);

      // As browsers don't support JPEG2000, when JPEG2000 file is uploaded we request .webp file instead from the server to display
      const fileName = currentImage.filename;
      const originalExtension = getExtensionFromFilename(fileName);
      const fallbackExtension = allowedImageUploads.WEBP.extension[0].replace('.', '');

      const $imgContainer = $(this.imageContainerTemplate);
      const $imgRef = $imgContainer.find('img');
      const imgHeight = $image.height() ?? IMG_FALLBACK_WIDTH;
      const imgWidth = $image.width() ?? IMG_FALLBACK_WIDTH;

      $imgRef.attr(
        'src',
        currentImage.getResizedUrl({
          format: isJPEG2000File({ filename: fileName })
            ? fallbackExtension
            : originalExtension ?? fallbackExtension,
          crop: 'fill',
          width: Math.round(imgWidth),
          height: Math.round(imgHeight),
        })
      );

      if (i === 0) {
        $image.append($(this.featuredTemplate));
      }
      $image.append($imgContainer).appendTo($tiledList);
      this.enableDragDrop($image);
      i += 1;
      currentImage = currentImage.next;
    }

    this.addEmptyTiles(numTiles);

    if (!this.options.readonly) {
      this.addUploadTile(numTiles);
    }
    this.initEventHandlers();
  };

  numberOfTilesToDisplay: MerchantosProductImageGridView['numberOfTilesToDisplay'] = () => {
    let numTiles = this.maxImages;
    const $imageGrid = this.$gridView.find('.product-image-grid');
    $imageGrid.removeClass('items2 items4 items6 items8');

    const numOfImages = this.productImages
      ? this.productImages.images.listSize + this.uploadsInProgress
      : 0;
    if (numOfImages < 2 || (numOfImages === 2 && this.options.readonly)) {
      numTiles = 2;
      $imageGrid.addClass('items2');
    } else if (numOfImages < 4 || (numOfImages === 4 && this.options.readonly)) {
      numTiles = 4;
      $imageGrid.addClass('items4');
    } else if (numOfImages < 6 || (numOfImages === 6 && this.options.readonly)) {
      numTiles = 6;
      $imageGrid.addClass('items6');
    } else if (numOfImages < 8 || (numOfImages === 8 && this.options.readonly)) {
      numTiles = 8;
      $imageGrid.addClass('items8');
    }

    return numTiles;
  };

  addEmptyTiles: MerchantosProductImageGridView['addEmptyTiles'] = (maxTiles) => {
    const $tiledList = this.$gridView.find('.product-image-list');

    const $imageTiles = $tiledList.find('li.image');
    const tilesToAdd = maxTiles - $imageTiles.length - 1; // account for image upload tile

    let i = 0;
    while (i < tilesToAdd) {
      const $image = $(this.imageTemplate).insertAfter($imageTiles.last());
      const $imgContainer = $(this.imageContainerTemplate).addClass('product-image-blank');
      $image.append($imgContainer);
      i += 1;
    }
  };

  addUploadTile: MerchantosProductImageGridView['addUploadTile'] = (
    maxTiles,
    $existingUploadView
  ) => {
    const $tiledList = this.$gridView.find('.product-image-list');

    // add upload tile if necessary
    if ($tiledList.children().length < maxTiles) {
      const $uploadView = $existingUploadView || $(this.uploadTemplate);
      $(this.imageTemplate).addClass('image-upload').append($uploadView).appendTo($tiledList);

      if (!$existingUploadView && !this.options.readonly) {
        this.createDropzone();
      }
    }
  };

  createDropzone: MerchantosProductImageGridView['createDropzone'] = () => {
    const $uploadTile = this.$gridView.find('div.upload').parent();
    if ($uploadTile.find('.upload').length > 0) {
      this.uploadView = new ProductImageUploadView($uploadTile, {
        type: this.options.type,
        imageType: this.options.imageType,
        hideProgress: true,
        nextOrder: this.productImages.images.listSize,
        productMatrix: this.options.productMatrix,
        currentImages: this.productImages.images.listSize,
      });
    }
  };

  handleGridResize: MerchantosProductImageGridView['handleGridResize'] = ($uploadTile: JQuery) => {
    const $upload = $uploadTile.parent();
    if ($upload.hasClass('image-upload')) {
      const $uploadContainer = $upload
        .removeClass('image-upload')
        .find('.image-upload-container-outer');
      const maxImagesToDisplay = this.numberOfTilesToDisplay();
      if (maxImagesToDisplay > this.productImages.images.listSize + this.uploadsInProgress) {
        this.addEmptyTiles(maxImagesToDisplay);
        // build a new tile for the upload container; we need to do this rather than just
        // remove the existing one and create a new one in order to preserve the correct behaviour
        // in the event that the upload into the upload view's tile is not the only upload in
        // progresses

        if (!this.options.readonly) {
          this.addUploadTile(maxImagesToDisplay, $uploadContainer);
          $uploadContainer.show(); // dropzone seems to hide itself when we move it within the DOM
        }
      }
    }
  };
}
